import type { PaymentKitEnvironment, PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import { collectFraudMetadata, definePaymentMethod, getOrCreateCheckoutRequestId } from "../utils";
import {
  AirwallexApplePayAdapter,
  type AirwallexApplePayConfig,
  AirwallexApplePayMockScenario,
  type ApplePayEncryptedToken,
} from "./airwallex-apple-pay-adapter";
import { handleNextAction } from "./next-action-handlers";
import { ApplePayMockScenario, StripeApplePayAdapter } from "./stripe-apple-pay-adapter";

// Apple Pay-specific types
export type ApplePayCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
};

export type ApplePayStartRequest = {
  processor_id: string;
  // Airwallex-only fields. Stripe /apple-pay/start is a pure read and
  // ignores these — customer_info/fraud_metadata are sent to /apple-pay/confirm
  // instead, where the customer + checkout attempt + PaymentIntent are created.
  customer_info?: ApplePayCustomerInfo;
  fraud_metadata?: {
    ipAddress?: string;
    browserInfo?: { [key: string]: unknown };
    processorFraudInfo?: { [key: string]: unknown };
  };
  mock_scenario?: string;
  // Airwallex-specific fields (sent on /start for merchant validation)
  validation_url?: string;
  initiative_context?: string;
};

export type ApplePayStartResponse = {
  amount: number;
  currency: string;
  country: string;

  // Stripe-specific (when calling /apple-pay/start)
  stripe_pk?: string;

  // Airwallex-specific (when calling /airwallex/apple-pay/start). Stripe's
  // /start does not create a checkout attempt — it's deferred to /confirm.
  checkout_attempt_id?: string;
  merchant_session?: { [key: string]: unknown }; // Apple merchant session for completeMerchantValidation()
};

// Airwallex 3DS next action type (matches backend snake_case response)
export type Airwallex3dsNextAction = {
  type: "airwallex_3ds";
  url: string;
};

export type ApplePayConfirmRequest = {
  // Stripe-only: moved from /start (where they used to drive customer/PI
  // creation). At confirm time, the Stripe path creates the customer, checkout
  // attempt, and PaymentIntent in a single call.
  processor_id?: string;
  customer_info?: ApplePayCustomerInfo;
  fraud_metadata?: {
    ipAddress?: string;
    browserInfo?: { [key: string]: unknown };
    processorFraudInfo?: { [key: string]: unknown };
  };
  // Stripe: Send PaymentMethod ID
  payment_method_id?: string;
  // Airwallex: Send encrypted Apple Pay token
  apple_pay_token?: ApplePayEncryptedToken | { [key: string]: unknown };
  // Email from payment sheet (Stripe Apple Pay) — used to fill missing customer email
  payer_email?: string;
  // Name from payment sheet — used to fill missing customer name
  payer_first_name?: string;
  payer_last_name?: string;
  mock_scenario?: string;
};

export type ApplePayConfirmResponse = {
  charge_status: "success" | "fail" | "pending";
  transaction_id?: string;
  error_code?: string;
  error_message_for_customer?: string;
  error_message_for_debug?: string;
  checkout_attempt_id: string;
  checkout_session_id?: string;
  next_action?: Airwallex3dsNextAction; // Present when charge_status="pending" for 3DS
  payment_intent_id?: string;
  customer_id?: string;
  payment_method_id?: string;
  processor_used?: string;
  subscription_id?: string;
  invoice_id?: string;
  invoice_number?: number;
  card_brand?: string;
  card_last4?: string;
  card_exp_month?: number;
  card_exp_year?: number;
};

export type ApplePaySubmitOptions = {
  /** Processor external ID — optional when using auto-prepare (SDK fetches from checkout session) */
  processorId?: string;
  processorType?: "stripe" | "airwallex";
  customerInfo: ApplePayCustomerInfo;
  mockScenario?: ApplePayMockScenario;
  amount?: number; // Amount in atomic units (cents) for Apple Pay sheet display
  currency?: string; // Currency code (e.g., "usd")
  /** Country code — optional when using auto-prepare (fetched from backend) */
  country?: string;
  merchantName?: string; // Merchant display name on Apple Pay sheet
};

type ApplePayResult =
  | { data: { [key: string]: unknown }; errors?: undefined }
  | { data?: undefined; errors: PaymentKitErrors };

// =============================================================================
// Prepared Payment State (for Stripe pre-fetching before click)
// =============================================================================

type PreparedStripeApplePayState = {
  processor: "stripe";
  adapter: StripeApplePayAdapter;
  startData: ApplePayStartResponse;
  mockScenarioStr?: string;
  checkoutRequestId: string;
};

// Global state to store prepared Apple Pay data (Stripe only - Airwallex uses native session)
let preparedStripeState: PreparedStripeApplePayState | null = null;

// Prepared Airwallex state — persists options from prepare call for use at submit time
// The amount field is fetched from the session via /session-info endpoint to fix
// the $0.00 bug where options.amount was undefined and defaulted to 0.
type PreparedAirwallexState = {
  country: string;
  currency?: string;
  amount?: number;
  merchantName?: string;
};
let preparedAirwallexState: PreparedAirwallexState | null = null;

// Response type for Airwallex Apple Pay session-info endpoint
type AirwallexSessionInfoResponse = {
  amount: number;
  currency: string;
  country: string;
};

// =============================================================================
// Auto-Prepare State
// =============================================================================

type AutoPrepareConfig = {
  processorId: string;
  processorType: "airwallex" | "stripe" | undefined;
};

type CheckoutSessionConfig = {
  express_checkout_processor_id?: string | null;
  express_checkout_processor_type?: string | null;
};

// =============================================================================
// Apple Pay SDK Loader
// =============================================================================

// Loads Apple's JS SDK, which makes window.ApplePaySession available in non-Safari
// browsers (Chrome, Firefox, etc.) and enables the "Scan Code with iPhone" QR flow.
let applePayScriptLoadPromise: Promise<void> | null = null;
async function loadApplePayScript(): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.ApplePaySession) return;
  if (document.getElementById("__pk_apple_pay_sdk")) {
    // Tag exists — if we have a cached promise, wait for it; otherwise the tag
    // already resolved (e.g. after a module reload) so return immediately.
    return applePayScriptLoadPromise ?? Promise.resolve();
  }
  applePayScriptLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = "__pk_apple_pay_sdk";
    script.src = "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
    script.crossOrigin = "anonymous";
    // NOTE: Apple doesn't publish stable SRI hashes for the 1.latest pointer,
    // so integrity checking is omitted. CSP must allow script-src https://applepay.cdn-apple.com.
    script.onload = () => resolve();
    script.onerror = () => {
      // Clear cached state so the next prepareApplePay call can retry.
      applePayScriptLoadPromise = null;
      document.getElementById("__pk_apple_pay_sdk")?.remove();
      reject(new Error("Failed to load Apple Pay SDK"));
    };
    document.head.appendChild(script);
  });
  return applePayScriptLoadPromise;
}

// =============================================================================
// Helper Functions
// =============================================================================

async function apiCall<T>(
  url: string,
  options: RequestInit,
  checkoutRequestId?: string,
): Promise<{ data?: T; error?: string }> {
  const headers = new Headers(options.headers);
  if (checkoutRequestId) {
    headers.set("x-request-id", checkoutRequestId);
  }

  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    return { error: errorMessage };
  }
  return { data: await response.json() };
}

function validateOptions(options: ApplePaySubmitOptions): PaymentKitErrors | null {
  if (!options?.processorId) {
    return { processor_id: "Processor ID is required" };
  }
  // For Airwallex, country is required (either directly or from prepared state)
  if (options.processorType === "airwallex" && !options.country && !preparedAirwallexState?.country) {
    return { country: "Country is required for Airwallex Apple Pay" };
  }
  return null;
}

function getMockScenarioStr(mockScenario?: ApplePayMockScenario): string | undefined {
  return mockScenario && mockScenario !== ApplePayMockScenario.None ? mockScenario : undefined;
}

// =============================================================================
// Stripe Apple Pay Flow
// =============================================================================

async function callStripeStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  checkoutRequestId?: string,
): Promise<{ data?: ApplePayStartResponse; error?: string }> {
  // /apple-pay/start is now a pure read: it returns stripe_pk + sheet data
  // (amount/currency/country). Customer/CheckoutAttempt/PaymentIntent are
  // created in /apple-pay/confirm once the user authorizes the sheet.
  return apiCall<ApplePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/apple-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
      } as ApplePayStartRequest),
    },
    checkoutRequestId,
  );
}

function initializeStripeAdapter(
  stripePk: string,
  mockScenario?: ApplePayMockScenario,
): { adapter?: StripeApplePayAdapter; error?: string } {
  const adapter = new StripeApplePayAdapter(mockScenario);

  if (!adapter.initialize(stripePk)) {
    return { error: 'Stripe.js not loaded. Add <script src="https://js.stripe.com/v3/"></script> to your page.' };
  }

  return { adapter };
}

async function callStripeConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  paymentMethodId: string,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
): Promise<ApplePayResult> {
  // /apple-pay/confirm now carries the fields that used to live on /start —
  // processor_id, customer_info, fraud_metadata — because the customer,
  // checkout attempt, and Stripe PaymentIntent are all created here.
  const result = await apiCall<ApplePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/apple-pay/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
        customer_info: options.customerInfo,
        fraud_metadata: collectFraudMetadata(),
        payment_method_id: paymentMethodId,
        payer_email: payerEmail,
        mock_scenario: mockScenarioStr,
      } as ApplePayConfirmRequest),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return { errors: { apple_pay: result.error || "Failed to confirm payment" } };
  }

  const confirmData = result.data;

  if (confirmData.charge_status === "success") {
    return {
      data: {
        id: confirmData.transaction_id,
        checkoutAttemptId: confirmData.checkout_attempt_id,
        checkoutSessionId: confirmData.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: confirmData.payment_intent_id,
        customerId: confirmData.customer_id,
        paymentMethodId: confirmData.payment_method_id,
        processorUsed: confirmData.processor_used,
        subscriptionId: confirmData.subscription_id,
        invoiceId: confirmData.invoice_id,
        invoiceNumber: confirmData.invoice_number,
        cardBrand: confirmData.card_brand,
        cardLast4: confirmData.card_last4,
        cardExpMonth: confirmData.card_exp_month,
        cardExpYear: confirmData.card_exp_year,
        errorCode: confirmData.error_code,
        errorMessageForCustomer: confirmData.error_message_for_customer,
        errorMessageForDebug: confirmData.error_message_for_debug,
        nextAction: confirmData.next_action,
      },
    };
  }

  return {
    errors: {
      apple_pay: confirmData.error_message_for_customer || confirmData.error_message_for_debug || "Payment failed",
    },
  };
}

// =============================================================================
// Prepare Function (call BEFORE the click handler)
// =============================================================================

/**
 * Pre-fetch Apple Pay data and prepare the PaymentRequest.
 * Call this when the checkout page loads, NOT in the click handler.
 *
 * For Stripe: pre-fetches data and prepares PaymentRequest for immediate show.
 * For Airwallex: returns success with processor="airwallex" so submit uses native ApplePaySession.
 *
 * @deprecated The SDK now calls this automatically on init. Use `onApplePayReady` to observe
 * readiness and `notifyAmountChanged` to trigger re-prepare after amount changes.
 */
export async function prepareApplePay(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  environment: PaymentKitEnvironment,
): Promise<{ success: boolean; error?: string; applePay?: boolean }> {
  console.log("[ApplePay] prepareApplePay called");

  // Clear any previous state
  preparedStripeState = null;

  const mockScenarioStr = getMockScenarioStr(options.mockScenario);
  const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
  console.log(`[ApplePay] Using checkout_request_id: ${checkoutRequestId}`);

  // Airwallex: Load Apple Pay SDK (enables QR code flow in non-Safari browsers),
  // then fetch session info to get the correct amount from line items.
  // This fixes the $0.00 bug where options.amount was undefined and defaulted to 0.
  if (options.processorType === "airwallex") {
    console.log("[ApplePay] Airwallex processor - loading Apple Pay SDK, fetching session info for amount");

    // In mock mode, skip SDK loading and availability check (jsdom has no ApplePaySession)
    if (!mockScenarioStr) {
      try {
        await loadApplePayScript();
      } catch (err) {
        console.error("[ApplePay] Failed to load Apple Pay SDK:", err);
        return { success: false, error: "Failed to load Apple Pay" };
      }

      let canMake = false;
      try {
        canMake = !!window.ApplePaySession?.canMakePayments();
      } catch {
        canMake = false;
      }
      if (!canMake) {
        console.log("[ApplePay] Apple Pay not available on this device/browser");
        return { success: false, error: "Apple Pay not available on this device" };
      }
    }

    // Fetch session info (amount, currency, country) from backend
    const sessionInfoResult = await callAirwallexSessionInfoEndpoint(apiBaseUrl, secureToken, checkoutRequestId);

    if (sessionInfoResult.error || !sessionInfoResult.data) {
      console.error("[ApplePay] Failed to fetch session info:", sessionInfoResult.error);
      return { success: false, error: sessionInfoResult.error || "Failed to fetch Apple Pay session info" };
    }

    console.log("[ApplePay] Got session info:", sessionInfoResult.data);
    const resolvedAmount = sessionInfoResult.data.amount;
    const resolvedCurrency = sessionInfoResult.data.currency;
    const resolvedCountry = sessionInfoResult.data.country || options.country;

    preparedAirwallexState = {
      country: resolvedCountry,
      currency: resolvedCurrency,
      amount: resolvedAmount,
      merchantName: options.merchantName,
    };
    console.log("[ApplePay] Airwallex prepared state:", preparedAirwallexState);
    return { success: true, applePay: true };
  }

  // Call Stripe start endpoint to fetch stripe_pk + sheet display data
  const startResult = await callStripeStartEndpoint(apiBaseUrl, secureToken, options, checkoutRequestId);
  if (startResult.error || !startResult.data) {
    return { success: false, error: startResult.error || "Failed to start Apple Pay" };
  }

  const startData = startResult.data;

  // Initialize Stripe adapter
  if (!startData.stripe_pk) {
    return { success: false, error: "Stripe publishable key not provided" };
  }

  const { adapter, error: adapterError } = initializeStripeAdapter(startData.stripe_pk, options.mockScenario);
  if (!adapter) {
    return { success: false, error: adapterError };
  }

  // Create PaymentRequest and call canMakePayment() (required before show())
  const prepareResult = await adapter.preparePaymentRequest({
    country: startData.country,
    currency: startData.currency.toLowerCase(),
    total: {
      label: "Total",
      amount: startData.amount,
    },
  });

  if (!prepareResult.success) {
    return {
      success: false,
      error: prepareResult.error || "Failed to prepare payment request",
      applePay: prepareResult.applePay,
    };
  }

  if (!prepareResult.applePay) {
    return {
      success: false,
      error: "Apple Pay is not available on this device or Stripe account",
      applePay: false,
    };
  }

  // Store the prepared state
  preparedStripeState = {
    processor: "stripe",
    adapter,
    startData,
    mockScenarioStr,
    checkoutRequestId,
  };

  console.log("[ApplePay] prepareApplePay success (Stripe), ready for click");
  return { success: true, applePay: true };
}

/**
 * Check if Apple Pay has been prepared and is ready.
 */
export function isApplePayPrepared(): boolean {
  return (preparedStripeState?.adapter.isPrepared() ?? false) || preparedAirwallexState !== null;
}

/**
 * Clear prepared state (call on unmount or when checkout changes).
 */
export function clearPreparedApplePay(): void {
  if (preparedStripeState) {
    preparedStripeState.adapter.clearPrepared();
  }
  preparedStripeState = null;
  preparedAirwallexState = null;
}

// =============================================================================
// Airwallex Apple Pay Flow
// =============================================================================

/**
 * Fetch session info (amount, currency, country) from the backend.
 * Called during prepareApplePay to get the correct amount BEFORE showing the payment sheet.
 * This fixes the $0.00 bug where options.amount was undefined and defaulted to 0.
 */
async function callAirwallexSessionInfoEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  checkoutRequestId?: string,
): Promise<{ data?: AirwallexSessionInfoResponse; error?: string }> {
  return apiCall<AirwallexSessionInfoResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/session-info`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
    checkoutRequestId,
  );
}

async function callAirwallexStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  validationUrl: string,
  initiativeContext: string,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
): Promise<{ data?: ApplePayStartResponse; error?: string }> {
  return apiCall<ApplePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
        customer_info: options.customerInfo,
        fraud_metadata: collectFraudMetadata(),
        validation_url: validationUrl,
        initiative_context: initiativeContext,
        mock_scenario: mockScenarioStr,
      } as ApplePayStartRequest),
    },
    checkoutRequestId,
  );
}

async function callAirwallexConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  applePayToken: ApplePayEncryptedToken,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
  payerFirstName?: string,
  payerLastName?: string,
): Promise<ApplePayConfirmResponse> {
  const result = await apiCall<ApplePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        apple_pay_token: applePayToken,
        payer_email: payerEmail,
        payer_first_name: payerFirstName,
        payer_last_name: payerLastName,
        mock_scenario: mockScenarioStr,
      } as ApplePayConfirmRequest),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message_for_debug: result.error || "Failed to confirm payment",
      checkout_attempt_id: "",
    };
  }

  return result.data;
}

async function callAirwallexVerifyEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  checkoutRequestId?: string,
): Promise<ApplePayConfirmResponse> {
  const result = await apiCall<ApplePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message_for_debug: result.error || "Failed to verify payment",
      checkout_attempt_id: "",
    };
  }

  return result.data;
}

function toApplePayResult(response: ApplePayConfirmResponse, secureToken: string): ApplePayResult {
  if (response.charge_status === "success") {
    return {
      data: {
        id: response.transaction_id,
        checkoutAttemptId: response.checkout_attempt_id,
        checkoutSessionId: response.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: response.payment_intent_id,
        customerId: response.customer_id,
        paymentMethodId: response.payment_method_id,
        processorUsed: response.processor_used,
        subscriptionId: response.subscription_id,
        invoiceId: response.invoice_id,
        invoiceNumber: response.invoice_number,
        cardBrand: response.card_brand,
        cardLast4: response.card_last4,
        cardExpMonth: response.card_exp_month,
        cardExpYear: response.card_exp_year,
        errorCode: response.error_code,
        errorMessageForCustomer: response.error_message_for_customer,
        errorMessageForDebug: response.error_message_for_debug,
        nextAction: response.next_action,
      },
    };
  }
  return {
    errors: {
      apple_pay: response.error_message_for_customer || response.error_message_for_debug || "Payment failed",
    },
  };
}

const MAX_USER_ACTIONS = 5;

/**
 * Run the Airwallex Apple Pay flow with 3DS loop support.
 *
 * Flow:
 * 1. Show native ApplePaySession, call /start for merchant validation
 * 2. On payment authorized, call /confirm with token
 * 3. If 3DS required, handle it and call /verify
 * 4. Loop if more 3DS needed
 */
async function runAirwallexFlow(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
): Promise<ApplePayResult> {
  // Convert mock scenario
  let airwallexMockScenario: AirwallexApplePayMockScenario = AirwallexApplePayMockScenario.None;
  if (options.mockScenario === ApplePayMockScenario.Success) {
    airwallexMockScenario = AirwallexApplePayMockScenario.Success;
  } else if (options.mockScenario === ApplePayMockScenario.Cancelled) {
    airwallexMockScenario = AirwallexApplePayMockScenario.Cancelled;
  }

  // Defense-in-depth: validateOptions should catch this earlier, but guard against
  // future callers that bypass validation to avoid an unhandled TypeError on `.toUpperCase()`
  if (!options.country) {
    return { errors: { apple_pay: "Country is required for Airwallex Apple Pay" } };
  }

  // Auto-fetch amount from session if not provided (e.g. merchant didn't call prepareApplePay first)
  if (!options.amount) {
    const sessionInfoResult = await callAirwallexSessionInfoEndpoint(apiBaseUrl, secureToken, checkoutRequestId);
    if (sessionInfoResult.data) {
      options.amount = sessionInfoResult.data.amount;
      options.currency = options.currency || sessionInfoResult.data.currency;
      options.country = options.country || sessionInfoResult.data.country;
    }
  }

  // Initialize adapter
  const adapter = new AirwallexApplePayAdapter(airwallexMockScenario);

  const config: AirwallexApplePayConfig = {
    amount: options.amount || 0,
    currency: (options.currency || "USD").toUpperCase(),
    country: options.country.toUpperCase(),
    merchantName: options.merchantName || "Total",
  };

  if (!adapter.initialize(config)) {
    return { errors: { apple_pay: "Apple Pay not available on this device" } };
  }

  const canPay = await adapter.canMakePayment();
  if (!canPay) {
    return { errors: { apple_pay: "Apple Pay not available on this device" } };
  }

  // Get current domain for initiative_context
  const initiativeContext = typeof window !== "undefined" ? window.location.hostname : "";

  // In mock mode, call /start first to create checkout attempt (mock adapter skips merchant validation)
  if (airwallexMockScenario !== AirwallexApplePayMockScenario.None) {
    const startResult = await callAirwallexStartEndpoint(
      apiBaseUrl,
      secureToken,
      options,
      "https://mock-apple-pay-gateway.example.com/paymentservices/startSession",
      initiativeContext,
      mockScenarioStr,
      checkoutRequestId,
    );
    if (startResult.error || !startResult.data) {
      return { errors: { apple_pay: startResult.error || "Failed to start mock checkout" } };
    }
  }

  // Show Apple Pay sheet with merchant validation callback
  const paymentResult = await adapter.showPaymentSheet(async (validationUrl: string) => {
    // Call /start endpoint for merchant validation
    const startResult = await callAirwallexStartEndpoint(
      apiBaseUrl,
      secureToken,
      options,
      validationUrl,
      initiativeContext,
      mockScenarioStr,
      checkoutRequestId,
    );

    if (startResult.error || !startResult.data) {
      return { error: startResult.error || "Merchant validation failed" };
    }

    if (!startResult.data.merchant_session) {
      return { error: "No merchant session returned from server" };
    }

    return { merchantSession: startResult.data.merchant_session };
  });

  if (!paymentResult.success) {
    if ("cancelled" in paymentResult && paymentResult.cancelled) {
      return { errors: { apple_pay: "Apple Pay cancelled by user" } };
    }
    return { errors: { apple_pay: paymentResult.error || "Apple Pay failed" } };
  }

  const { token, complete, payerEmail, payerFirstName, payerLastName } = paymentResult;

  // Call /confirm with the token
  let response = await callAirwallexConfirmEndpoint(
    apiBaseUrl,
    secureToken,
    token,
    mockScenarioStr,
    checkoutRequestId,
    payerEmail,
    payerFirstName,
    payerLastName,
  );

  // Handle 3DS loop
  let userActionCount = 0;
  while (response.charge_status === "pending" && response.next_action && userActionCount < MAX_USER_ACTIONS) {
    userActionCount++;

    // Handle 3DS challenge — Airwallex3dsNextAction is structurally identical to SDK's Airwallex3dsAction
    const nextAction = response.next_action as Airwallex3dsNextAction;
    const actionResult = await handleNextAction({ type: nextAction.type, url: nextAction.url });

    // Call verify endpoint
    const verifyResponse = await callAirwallexVerifyEndpoint(apiBaseUrl, secureToken, checkoutRequestId);

    // Check if another 3DS action is required
    if (verifyResponse.charge_status === "pending" && verifyResponse.next_action) {
      if (!actionResult.success) {
        console.log("[ApplePay:Airwallex] 3DS failed but retry available, continuing loop...");
      }
      response = verifyResponse;
      continue;
    }

    // No more actions - check final result
    if (!actionResult.success) {
      complete("fail");
      return {
        errors: {
          apple_pay:
            verifyResponse.error_message_for_customer ||
            verifyResponse.error_message_for_debug ||
            actionResult.error ||
            "3DS authentication failed",
        },
      };
    }

    // 3DS succeeded
    complete(verifyResponse.charge_status === "success" ? "success" : "fail");
    return toApplePayResult(verifyResponse, secureToken);
  }

  // Check for max attempts exceeded
  if (userActionCount >= MAX_USER_ACTIONS) {
    complete("fail");
    return { errors: { apple_pay: "Too many authentication attempts. Please try again." } };
  }

  // No 3DS required - complete with result
  complete(response.charge_status === "success" ? "success" : "fail");
  return toApplePayResult(response, secureToken);
}

// =============================================================================
// Main Submit Function
// =============================================================================

const defSubmitPayment = (
  states: PaymentKitStates,
  getAutoPrepareConfig: () => AutoPrepareConfig | null,
  onClearPrepared: () => void,
) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (_fields, options) => {
    const { apiBaseUrl, secureToken, environment } = states;
    const applePayOptions = options as ApplePaySubmitOptions;

    // Fill in processorId/processorType from auto-prepare config if merchant didn't provide them
    const autoPrepareConfig = getAutoPrepareConfig();
    if (!applePayOptions.processorId && autoPrepareConfig?.processorId) {
      applePayOptions.processorId = autoPrepareConfig.processorId;
    }
    if (!applePayOptions.processorType && autoPrepareConfig?.processorType) {
      applePayOptions.processorType = autoPrepareConfig.processorType;
    }

    // Validate options
    const validationError = validateOptions(applePayOptions);
    if (validationError) {
      return { errors: validationError };
    }

    try {
      const mockScenarioStr = getMockScenarioStr(applePayOptions.mockScenario);
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      console.log(`[ApplePay] Using checkout_request_id: ${checkoutRequestId}`);

      // Check if we have prepared Stripe state
      if (preparedStripeState?.adapter.isPrepared()) {
        console.log("[ApplePay] Using prepared Stripe state for immediate sheet display");

        // Show Apple Pay sheet IMMEDIATELY using the prepared PaymentRequest
        const paymentResultPromise = preparedStripeState.adapter.showPreparedPaymentSheet();
        const paymentResult = await paymentResultPromise;

        if (!paymentResult.success) {
          clearPreparedApplePay();

          if ("cancelled" in paymentResult && paymentResult.cancelled) {
            return { errors: { apple_pay: "Apple Pay cancelled by user" } };
          }
          const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
          return { errors: { apple_pay: errorMessage } };
        }

        const { paymentMethodId, paymentMethodEvent, billingDetails } = paymentResult;

        // Confirm with backend using the PaymentMethod ID
        const confirmResult = await callStripeConfirmEndpoint(
          apiBaseUrl,
          secureToken,
          applePayOptions,
          paymentMethodId,
          mockScenarioStr,
          preparedStripeState.checkoutRequestId,
          billingDetails?.email,
        );

        // Complete Apple Pay UI based on result
        if (confirmResult.data) {
          paymentMethodEvent.complete("success");
          clearPreparedApplePay();
          return confirmResult;
        } else {
          paymentMethodEvent.complete("fail");
          clearPreparedApplePay();
          return confirmResult;
        }
      }

      // Check if this is an Airwallex processor - route directly to Airwallex flow
      if (applePayOptions.processorType === "airwallex") {
        console.log("[ApplePay] Running Airwallex flow (processorType=airwallex)");
        // Merge prepare-time state into submit options so country/currency/amount/merchantName
        // are available even if the merchant only provided them at prepare time
        if (preparedAirwallexState) {
          if (applePayOptions.country == null) applePayOptions.country = preparedAirwallexState.country;
          if (applePayOptions.currency == null) applePayOptions.currency = preparedAirwallexState.currency;
          if (applePayOptions.amount == null) applePayOptions.amount = preparedAirwallexState.amount;
          if (applePayOptions.merchantName == null) applePayOptions.merchantName = preparedAirwallexState.merchantName;
        }
        try {
          return await runAirwallexFlow(apiBaseUrl, secureToken, applePayOptions, mockScenarioStr, checkoutRequestId);
        } finally {
          preparedAirwallexState = null;
        }
      }

      // No prepared state for Stripe - call start endpoint
      const startResult = await callStripeStartEndpoint(apiBaseUrl, secureToken, applePayOptions, checkoutRequestId);

      if (startResult.error || !startResult.data) {
        return { errors: { apple_pay: startResult.error || "Failed to start Apple Pay" } };
      }

      const startData = startResult.data;

      // Stripe flow without prepared state - this may fail due to user gesture requirements
      console.warn("[ApplePay] Stripe flow without prepared state - may fail due to user gesture requirements");

      if (!startData.stripe_pk) {
        return { errors: { apple_pay: "Stripe publishable key not provided" } };
      }

      const { adapter, error: adapterError } = initializeStripeAdapter(
        startData.stripe_pk,
        applePayOptions.mockScenario,
      );
      if (!adapter) {
        return { errors: { apple_pay: adapterError || "Failed to initialize Stripe adapter" } };
      }

      // This will likely fail due to user gesture requirements
      const prepareResult = await adapter.preparePaymentRequest({
        country: startData.country,
        currency: startData.currency.toLowerCase(),
        total: { label: "Total", amount: startData.amount },
      });

      if (!prepareResult.success || !prepareResult.applePay) {
        return { errors: { apple_pay: prepareResult.error || "Apple Pay not available" } };
      }

      const paymentResult = await adapter.showPreparedPaymentSheet();

      if (!paymentResult.success) {
        if ("cancelled" in paymentResult && paymentResult.cancelled) {
          return { errors: { apple_pay: "Apple Pay cancelled by user" } };
        }
        const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
        return { errors: { apple_pay: errorMessage } };
      }

      const { paymentMethodId, paymentMethodEvent, billingDetails } = paymentResult;

      const confirmResult = await callStripeConfirmEndpoint(
        apiBaseUrl,
        secureToken,
        applePayOptions,
        paymentMethodId,
        mockScenarioStr,
        checkoutRequestId,
        billingDetails?.email,
      );

      if (confirmResult.data) {
        paymentMethodEvent.complete("success");
        return confirmResult;
      } else {
        paymentMethodEvent.complete("fail");
        return confirmResult;
      }
    } catch (error) {
      onClearPrepared();
      return { errors: { apple_pay: `Apple Pay error: ${error}` } };
    }
  };

  return submitPayment;
};

// =============================================================================
// Payment Method Definition
// =============================================================================

const ApplePayPaymentMethod = definePaymentMethod((paymentKitStates) => {
  const { apiBaseUrl, secureToken, environment } = paymentKitStates;

  // Per-instance auto-prepare state — isolated from other ApplePayPaymentMethod instances.
  // Note: preparedStripeState and preparedAirwallexState remain module-level globals (pre-existing
  // architectural constraint shared across the file). Full per-instance isolation for those fields
  // would require threading them through prepareApplePay, runAirwallexFlow, validateOptions, and
  // defSubmitPayment — tracked as tech debt.
  let autoPrepareConfig: AutoPrepareConfig | null = null;
  let onApplePayReadyCallbacks: ((isReady: boolean) => void)[] = [];
  let isApplePayReadyState = false;
  let autoPrepareInProgress = false;
  // Incremented by cleanup() so that in-flight prepares can detect staleness and skip writes.
  let cleanupGeneration = 0;
  // Stores pending retry args when notifyAmountChanged is called while a prepare is in flight.
  let pendingAutoPrepareArgs: { apiBaseUrl: string; secureToken: string; environment: PaymentKitEnvironment } | null =
    null;
  // Resolvers waiting for a queued retry to complete (for correct notifyAmountChanged await).
  let pendingPrepareResolvers: (() => void)[] = [];

  function instanceSetApplePayReady(ready: boolean): void {
    isApplePayReadyState = ready;
    for (const cb of onApplePayReadyCallbacks) cb(ready);
  }

  function instanceClearPreparedApplePay(): void {
    clearPreparedApplePay();
    instanceSetApplePayReady(false);
  }

  async function instanceAutoPrepare(apBase: string, sToken: string, env: PaymentKitEnvironment): Promise<void> {
    if (autoPrepareInProgress) {
      // A prepare is already in flight; queue a retry and return a promise that resolves after it.
      pendingAutoPrepareArgs = { apiBaseUrl: apBase, secureToken: sToken, environment: env };
      return new Promise<void>((resolve) => pendingPrepareResolvers.push(resolve));
    }
    autoPrepareInProgress = true;
    // Capture generation so we can detect if cleanup() is called while we await.
    const capturedGeneration = cleanupGeneration;
    instanceSetApplePayReady(false);

    try {
      const result = await apiCall<CheckoutSessionConfig>(`${apBase}/api/checkout-sessions/token/${sToken}`, {
        method: "GET",
      });

      if (cleanupGeneration !== capturedGeneration) return;

      if (result.error || !result.data) {
        console.warn("[ApplePay] Auto-prepare: failed to fetch session config:", result.error);
        return;
      }

      const processorId = result.data.express_checkout_processor_id;
      if (!processorId) {
        console.log("[ApplePay] Auto-prepare: no express checkout processor configured");
        return;
      }

      const processorTypeRaw = result.data.express_checkout_processor_type;
      const processorType =
        processorTypeRaw === "airwallex" || processorTypeRaw === "stripe" ? processorTypeRaw : undefined;

      autoPrepareConfig = { processorId, processorType };

      const prepareResult = await prepareApplePay(
        apBase,
        sToken,
        { processorId, processorType, customerInfo: { first_name: "", last_name: "" } },
        env,
      );

      if (cleanupGeneration !== capturedGeneration) return;

      instanceSetApplePayReady(prepareResult.success && prepareResult.applePay === true);
    } catch (err) {
      if (cleanupGeneration === capturedGeneration) {
        console.error("[ApplePay] Auto-prepare failed:", err);
      }
    } finally {
      autoPrepareInProgress = false;
      const stale = cleanupGeneration !== capturedGeneration;
      const pending = pendingAutoPrepareArgs;
      const resolvers = pendingPrepareResolvers.splice(0);
      if (!stale && pending) {
        pendingAutoPrepareArgs = null;
        // Run the retry and resolve all callers that were waiting for it.
        instanceAutoPrepare(pending.apiBaseUrl, pending.secureToken, pending.environment).then(() => {
          for (const r of resolvers) r();
        });
      } else {
        pendingAutoPrepareArgs = null;
        for (const r of resolvers) r();
      }
    }
  }

  // Start auto-prepare immediately so the button is ready before first user interaction.
  instanceAutoPrepare(apiBaseUrl, secureToken, environment).catch((err) =>
    console.error("[ApplePay] Unhandled init error:", err),
  );

  return {
    name: "apple_pay",
    externalFuncs: {
      /**
       * @deprecated The SDK now prepares automatically on init. Use `onApplePayReady` to observe
       * readiness and `notifyAmountChanged` to trigger re-prepare after amount changes.
       */
      prepareApplePay: (options: ApplePaySubmitOptions) =>
        prepareApplePay(apiBaseUrl, secureToken, options, environment),

      /**
       * Register a callback to be notified when Apple Pay becomes ready (or stops being ready).
       * Fires immediately with the current state, then on every state change.
       * Multiple registrations are all notified — none are silently overwritten.
       *
       * @example
       * paymentKit.apple_pay.onApplePayReady((isReady) => {
       *   applePayButton.disabled = !isReady;
       * });
       */
      onApplePayReady: (callback: (isReady: boolean) => void): void => {
        onApplePayReadyCallbacks.push(callback);
        // Fire immediately with current state so caller doesn't miss a completed prepare.
        callback(isApplePayReadyState);
      },

      /**
       * Notify the SDK that the payment amount has changed (e.g. coupon applied).
       * The SDK will disable Apple Pay, re-prepare with the new amount, then re-enable.
       * The returned promise resolves when re-prepare is fully complete.
       *
       * @example
       * await paymentKit.apple_pay.notifyAmountChanged();
       */
      notifyAmountChanged: (): Promise<void> => {
        instanceClearPreparedApplePay();
        autoPrepareConfig = null;
        return instanceAutoPrepare(apiBaseUrl, secureToken, environment);
      },
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(paymentKitStates, () => autoPrepareConfig, instanceClearPreparedApplePay),
      cleanup: () => {
        cleanupGeneration++;
        onApplePayReadyCallbacks = [];
        isApplePayReadyState = false;
        autoPrepareConfig = null;
        pendingAutoPrepareArgs = null;
        pendingPrepareResolvers = [];
        autoPrepareInProgress = false;
        clearPreparedApplePay();
      },
    },
  };
});

export { ApplePayMockScenario };

export default ApplePayPaymentMethod;
